import { Link } from "react-router-dom";
import { Trash2, AlertTriangle, ShoppingBag } from "lucide-react";
import { useMemo } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { uploadUrl } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";
import { testIds } from "@/constants/testIds/b2b";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, totalUnits, moqViolations, minOrderUnits, belowMinOrder, unitsToMin } = useCart();
  const { isApproved, user } = useAuth();

  // Group by product for the order pad
  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      if (!map.has(it.product_id)) {
        map.set(it.product_id, {
          product_id: it.product_id,
          name: it.name,
          image: it.image,
          wholesale_price: it.wholesale_price,
          moq: it.moq,
          rows: [],
        });
      }
      map.get(it.product_id).rows.push(it);
    });
    return Array.from(map.values());
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-24 text-center" data-testid={testIds.cartEmpty}>
        <ShoppingBag size={28} strokeWidth={1.5} className="mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-semibold mb-2">Your order pad is empty</h1>
        <p className="text-sm text-muted-foreground mb-6">Add styles from the products page to build your order.</p>
        <Link to="/shop" className="inline-flex px-5 py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
          Browse products
        </Link>
      </div>
    );
  }

  return (
    <div className="page-card" data-testid={testIds.cartPage}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Order pad</p>
            <h1 className="text-2xl md:text-3xl font-semibold mt-0.5">Review your order</h1>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Subtotal</p>
            <p data-testid={testIds.cartSubtotal} className="text-2xl font-semibold tabular-nums">{fmtCAD(subtotal)}</p>
            <p className="text-xs text-muted-foreground tabular-nums">{totalUnits} units</p>
          </div>
        </div>

        <div className="border border-border rounded-md overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 bg-secondary border-b border-border text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            <div className="col-span-5">Style</div>
            <div className="col-span-1">Size</div>
            <div className="col-span-2 text-right">Unit</div>
            <div className="col-span-2 text-center">Qty</div>
            <div className="col-span-2 text-right">Line</div>
          </div>
          {grouped.map((g, gi) => (
            <div key={g.product_id} className={gi > 0 ? "border-t border-border" : ""}>
              {g.rows.map((it, ri) => {
                const line = (it.wholesale_price || 0) * it.quantity;
                return (
                  <div
                    key={`${it.product_id}::${it.size}`}
                    data-testid={testIds.cartItem}
                    className={`grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm ${ri > 0 ? "border-t border-border/60" : ""}`}
                  >
                    <div className="col-span-12 md:col-span-5 flex items-center gap-3">
                      <div className="w-12 h-14 rounded bg-secondary overflow-hidden shrink-0">
                        {it.image && <img src={uploadUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{it.name}</p>
                      </div>
                    </div>
                    <div className="col-span-3 md:col-span-1 text-muted-foreground">{it.size || "—"}</div>
                    <div className="col-span-3 md:col-span-2 text-right tabular-nums">{fmtCAD(it.wholesale_price)}</div>
                    <div className="col-span-3 md:col-span-2 flex items-center justify-center gap-1.5">
                      <input
                        data-testid={`cart-qty-${it.product_id}`}
                        type="number"
                        min={0}
                        value={it.quantity}
                        onChange={(e) => updateQty(it.product_id, it.size, e.target.value)}
                        className="w-16 px-2 py-1.5 text-sm text-center border rounded tabular-nums focus:border-primary border-border"
                      />
                    </div>
                    <div className="col-span-2 md:col-span-2 flex items-center justify-end gap-2 tabular-nums">
                      <span>{fmtCAD(line)}</span>
                      <button
                        onClick={() => removeItem(it.product_id, it.size)}
                        className="p-1 text-muted-foreground hover:text-destructive"
                        data-testid={`cart-remove-${it.product_id}`}
                        aria-label="Remove"
                      >
                        <Trash2 size={13} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {belowMinOrder && (
          <div className="mt-4 px-3 py-2.5 rounded border border-destructive/40 bg-destructive/5 text-sm text-destructive flex items-start gap-2" data-testid={testIds.cartMoqWarning}>
            <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <span>Minimum order is {minOrderUnits} units — add {unitsToMin} more before checking out.</span>
          </div>
        )}
        {items.length > 0 && !belowMinOrder && (items[0].fulfillment_type || "prebook") === "prebook" && (
          <p className="mt-3 text-xs text-muted-foreground">Minimum order: {minOrderUnits} units. You have {totalUnits}.</p>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <Link to="/shop" className="text-sm text-muted-foreground hover:text-foreground">
            ← Continue shopping
          </Link>
          {!isApproved ? (
            <div className="text-sm text-muted-foreground text-right max-w-md">
              {user ? "Your account is pending approval. You'll be able to submit orders once approved." : <>Approved buyers only. <Link to="/login" className="text-primary underline">Sign in</Link> or <Link to="/register" className="text-primary underline">apply</Link>.</>}
            </div>
          ) : (
            <Link
              to="/checkout"
              data-testid={testIds.cartCheckout}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity ${belowMinOrder ? "pointer-events-none opacity-40" : ""}`}
            >
              Continue to checkout
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
